import { Filter, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/shadcn/popover";
import { Separator } from "@/components/shadcn/separator";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Checkbox } from "@/components/shadcn/checkbox";
import type { LookupResponse } from "@/api/model";

interface Props {
  icon?: LucideIcon;
  label: string;
  titleEmptyOptions?: string;
  availableOptions: LookupResponse[] | string[] | undefined;
  selectedOptions?: string[];
  onSelect: (values: string[]) => void;
  onReset: () => void;
}

export function FilterDropdown({ icon, label, titleEmptyOptions, availableOptions, selectedOptions, onSelect, onReset }: Props) {
  const { t } = useTranslation('filters');
  const CustomIcon = icon || Filter;

  const selectedValues = selectedOptions || [];

  const handleCheckedChange = (checked: boolean, id: string) => {
    onSelect(checked ? [...selectedValues, id] : selectedValues.filter((v) => v !== id));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-2">
          <CustomIcon className="h-4 w-4" />
          <span className="text-xs">{label}</span>
          <Separator orientation="vertical" className="h-5" />
          <Badge className="min-w-5 justify-center rounded-sm">
            {selectedValues.length}
          </Badge>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto gap-2" align="start">
        {availableOptions === undefined || availableOptions.length === 0 
          ? (<div className="text-sm font-medium leading-none flex-1 p-1">
              {titleEmptyOptions || t('dropdown.empty')}
            </div>) 
          : (<div className="max-h-75 overflow-y-auto scrollbar-custom">
              <div className="flex flex-col gap-1 overflow-hidden">
                {availableOptions.map((option) => {
                  // Check if Enum or LookupResponse
                  const id = typeof option === 'string' ? option : option.id
                  const name = typeof option === 'string' ? option : option.name
                  if (!id) return null

                  return (
                    <div key={id} className="flex items-center w-full gap-3 p-1.5 rounded transition-colors group cursor-pointer select-none">
                      <Checkbox
                        id={`filter-${id}`}
                        checked={selectedValues.includes(id)}
                        onCheckedChange={(checked) => handleCheckedChange(checked === true, id)}
                        className="cursor-pointer"
                      />
                      <div className="cursor-text select-text">{name}</div>
                    </div>
                  )
                })}
              </div>
            </div>)}

        {selectedValues.length > 0 && (
          <>
            <Separator />
            <div className="flex items-center">
              <Button
                variant="destructive"
                className="w-full justify-center text-xs h-8"
                onClick={onReset}
              >
                {t('dropdown.reset')}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
